import React, { createContext, useState, useEffect, useContext, useCallback, useRef } from 'react';
import { AuthContext } from './AuthContext';
import { DEFAULT_PREPARATION_DAYS } from '../lib/preparationTime';

export const CartContext = createContext();

const isRemovedDummyProduct = (item) => {
  const productId = Number(item?.product_id || item?.id);
  const sku = String(item?.sku || '').trim().toUpperCase();
  const slug = String(item?.slug || '').trim().toLowerCase();
  return productId === 7 || sku === 'TEST-PAY-001' || slug === 'mini-test-checkout-item' || item?.status === 'draft';
};

const removeDummyProductsFromGuestCart = (items) => (
  Array.isArray(items) ? items.filter((item) => !isRemovedDummyProduct(item)) : []
);

const getProductId = (item) => item?.product_id ?? item?.productId ?? item?.id;
const getVariantId = (item) => item?.variant_id ?? item?.variantId ?? null;
const sameCartItem = (left, right) => (
  String(getProductId(left)) === String(getProductId(right))
  && String(getVariantId(left) ?? '') === String(getVariantId(right) ?? '')
);
const cartItemQuery = (variantId) => variantId === null || variantId === undefined || variantId === ''
  ? ''
  : `?variant_id=${encodeURIComponent(variantId)}`;

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const [cartLoading, setCartLoading] = useState(true);
  const hydrationRef = useRef(0);
  const { user, token, loading: authLoading } = useContext(AuthContext);

  const fetchCartFromBackend = useCallback(async () => {
    const res = await fetch('/api/cart', {
      headers: { 'x-auth-token': token },
    });
    if (!res.ok) throw new Error(`Cart request failed with status ${res.status}`);
    return removeDummyProductsFromGuestCart(await res.json());
  }, [token]);

  const refreshCartFromBackend = useCallback(async () => {
    try {
      setCartItems(await fetchCartFromBackend());
    } catch (err) {
      console.error('Failed to fetch cart from backend:', err);
    }
  }, [fetchCartFromBackend]);

  useEffect(() => {
    const hydrationId = hydrationRef.current + 1;
    hydrationRef.current = hydrationId;
    let active = true;

    if (authLoading) {
      setCartLoading(true);
      return () => {
        active = false;
      };
    }

    setCartLoading(true);

    if (user && token) {
      fetchCartFromBackend()
        .then((items) => {
          if (active && hydrationRef.current === hydrationId) setCartItems(items);
        })
        .catch((err) => {
          if (active && hydrationRef.current === hydrationId) {
            console.error('Failed to hydrate cart from backend:', err);
            setCartItems([]);
          }
        })
        .finally(() => {
          if (active && hydrationRef.current === hydrationId) setCartLoading(false);
        });
      return () => {
        active = false;
      };
    }

    const localCart = localStorage.getItem('guest_cart');
    if (!localCart) {
      setCartItems([]);
      setCartLoading(false);
      return () => {
        active = false;
      };
    }

    try {
      const storedItems = JSON.parse(localCart);
      const cleanedItems = removeDummyProductsFromGuestCart(storedItems);
      setCartItems(cleanedItems);
      if (cleanedItems.length !== storedItems.length) {
        localStorage.setItem('guest_cart', JSON.stringify(cleanedItems));
      }
    } catch (_err) {
      setCartItems([]);
    }
    setCartLoading(false);
    return () => {
      active = false;
    };
  }, [authLoading, user, token, fetchCartFromBackend]);

  const saveCartLocally = (items) => {
    setCartItems(items);
    if (!user) {
      localStorage.setItem('guest_cart', JSON.stringify(items));
    }
  };

  const addToCart = async (product, quantity = 1) => {
    const cartItem = {
      product_id: product.id,
      id: product.id,
      variant_id: product.variant_id ?? product.variantId ?? null,
      variant_name: product.variant_name ?? product.variantName ?? '',
      title: product.title,
      sku: product.variant_sku ?? product.sku ?? '',
      price_mrp: product.price_mrp,
      final_price: product.final_price || product.price_mrp,
      preparation_days: product.preparation_days ?? DEFAULT_PREPARATION_DAYS,
      cover_image: product.images?.[0] || 'https://images.unsplash.com/photo-1584992236310-6edddc08acff?auto=format&fit=crop&w=800&q=80',
      quantity,
    };
    const existingIndex = cartItems.findIndex((item) => sameCartItem(item, cartItem));

    let updatedCart = [...cartItems];
    if (existingIndex > -1) {
      const current = updatedCart[existingIndex];
      const newQty = (current.quantity || 1) + quantity;
      updatedCart[existingIndex] = { ...current, quantity: newQty };
    } else {
      updatedCart.push(cartItem);
    }

    saveCartLocally(updatedCart);

    if (user && token) {
      try {
        const response = await fetch('/api/cart', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-auth-token': token,
          },
          body: JSON.stringify({
            productId: product.id,
            product_id: product.id,
            variant_id: cartItem.variant_id,
            quantity,
          }),
        });
        if (!response.ok) await refreshCartFromBackend();
      } catch (err) {
        console.error('Failed to save cart item to server:', err);
        await refreshCartFromBackend();
      }
    }
  };

  const removeFromCart = async (productId, variantId = null) => {
    const target = { product_id: productId, variant_id: variantId };
    const updated = cartItems.filter((item) => !sameCartItem(item, target));
    saveCartLocally(updated);

    if (user && token) {
      try {
        const response = await fetch(`/api/cart/${productId}${cartItemQuery(variantId)}`, {
          method: 'DELETE',
          headers: { 'x-auth-token': token },
        });
        if (!response.ok) await refreshCartFromBackend();
      } catch (err) {
        console.error('Failed to remove cart item from server:', err);
        await refreshCartFromBackend();
      }
    }
  };

  const updateQuantity = async (productId, delta, variantId = null) => {
    const target = { product_id: productId, variant_id: variantId };
    const updated = cartItems.map((item) => {
      if (sameCartItem(item, target)) {
        const newQty = Math.max(1, (item.quantity || 1) + delta);
        return { ...item, quantity: newQty };
      }
      return item;
    });
    saveCartLocally(updated);

    if (user && token) {
      const changedItem = updated.find((item) => sameCartItem(item, target));
      if (!changedItem) return;

      try {
        const response = await fetch(`/api/cart/${productId}${cartItemQuery(getVariantId(changedItem))}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'x-auth-token': token,
          },
          body: JSON.stringify({ quantity: changedItem.quantity || 1, variant_id: getVariantId(changedItem) }),
        });
        if (!response.ok) await refreshCartFromBackend();
      } catch (err) {
        console.error('Failed to update cart quantity on server:', err);
        await refreshCartFromBackend();
      }
    }
  };

  const clearCart = async () => {
    setCartItems([]);
    localStorage.removeItem('guest_cart');

    if (user && token) {
      try {
        const response = await fetch('/api/cart', {
          method: 'DELETE',
          headers: { 'x-auth-token': token },
        });
        if (!response.ok) await refreshCartFromBackend();
      } catch (err) {
        console.error('Failed to clear cart on server:', err);
        await refreshCartFromBackend();
      }
    }
  };

  return (
    <CartContext.Provider
      value={{
        cartItems,
        cartLoading,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
